/**
 * Silent access-token refresh.
 *
 * The access token is short lived. Instead of logging the user out when it expires we
 *   1. refresh it in the background before it expires (startBackgroundTokenRefresh), and
 *   2. when any axios call still comes back "expired", refresh once and retry that call
 *      transparently (installAuthRefresh). RTK Query has the same logic in customFetchBase.
 * The user is only sent to the sign-in page when the REFRESH token itself is gone/expired.
 */
import axios, { AxiosError } from "axios";
import { getEmbeddedToken } from "./shoplineEmbedded";

const API_BASE = (process.env.REACT_APP_SERVER_ENDPOINT || "").replace(/\/+$/, "");
const REFRESH_URL = `${API_BASE}/api/auth/refresh`;

/** 401 messages that mean "access token missing/expired" (FastAPI sends them as `detail`) */
export const AUTH_EXPIRED_MESSAGES = ["You are not logged in", "Token is invalid or has expired"];

export const isAuthExpiredResponse = (status?: number, data?: any): boolean => {
  const msg = data?.detail ?? data?.message;
  return status === 401 && typeof msg === "string" && AUTH_EXPIRED_MESSAGES.includes(msg);
};

const isApiUrl = (url: string): boolean => {
  if (!API_BASE) return false;
  try { return new URL(url, window.location.href).href.startsWith(API_BASE); } catch { return false; }
};

const isLoggedIn = (): boolean => {
  try {
    return localStorage.getItem("logged_in") === "true" || !!localStorage.getItem("user_email");
  } catch { return false; }
};

// ------------------------------------------------------------------ refresh (one at a time)
let refreshPromise: Promise<number> | null = null;
let lastRefreshAt = 0;
let refreshEveryMs = 10 * 60 * 1000;   // updated from the server's token lifetime

/** Ask the server for a new access token (uses the refresh-token cookie). */
export const refreshAccessToken = (): Promise<number> => {
  if (!refreshPromise) {
    refreshPromise = axios
      .get(REFRESH_URL, { withCredentials: true, __skipAuthRefresh: true } as any)
      .then((res) => {
        lastRefreshAt = Date.now();
        const expiresIn = Number(res?.data?.expires_in) || 0;
        // refresh again when 80% of the lifetime has passed (at least every minute)
        if (expiresIn > 0) refreshEveryMs = Math.max(60_000, Math.floor(expiresIn * 0.8 * 1000));
        return expiresIn;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
};

/** Session really ended (refresh token gone/expired): clear everything and go to sign-in. */
export const endSession = () => {
  if (!isLoggedIn()) return;
  try {
    localStorage.clear();
    localStorage.setItem("logged_in", "false");
  } catch { /* ignore */ }
  window.location.href = "/signin";
};

// ------------------------------------------------------------------ axios: refresh + retry
let interceptorInstalled = false;

export const installAuthRefresh = () => {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const cfg: any = error.config;
      if (
        !cfg ||
        cfg.__skipAuthRefresh ||
        cfg.__authRetried ||
        getEmbeddedToken() ||                       // SHOPLINE iframe uses its own session token
        !isAuthExpiredResponse(error.response?.status, error.response?.data)
      ) {
        return Promise.reject(error);
      }
      const full = /^https?:\/\//i.test(cfg.url || "") ? cfg.url : `${cfg.baseURL || ""}${cfg.url || ""}`;
      if (!isApiUrl(full)) return Promise.reject(error);

      cfg.__authRetried = true;
      try {
        await refreshAccessToken();
      } catch {
        endSession();
        return Promise.reject(error);
      }
      return axios(cfg);                            // retry the original request once
    }
  );
};

// ------------------------------------------------------------------ background refresh
let backgroundStarted = false;

const maybeRefresh = () => {
  if (!isLoggedIn() || getEmbeddedToken()) return;
  if (Date.now() - lastRefreshAt < refreshEveryMs) return;
  refreshAccessToken().catch(() => { /* a real expiry is handled by the next API call */ });
};

export const startBackgroundTokenRefresh = () => {
  if (backgroundStarted) return;
  backgroundStarted = true;

  maybeRefresh();                                              // right after the page loads
  window.setInterval(maybeRefresh, 60_000);                    // keeps going while the tab is open
  document.addEventListener("visibilitychange", () => {        // tab was asleep: catch up at once
    if (!document.hidden) maybeRefresh();
  });
  window.addEventListener("online", maybeRefresh);
};
